<template>
    <div class="relative overflow-hidden">
        <picture v-if="!props.src.endsWith('.svg')">
            <source
                :srcset="srcset"
                :sizes="props.sizes"
                :media="`(min-width: ${minWidth}px)`"
            />
            <NuxtImg
                :src="defaultUrl"
                :fetchpriority="preload ? 'high' : undefined"
                :loading="preload ? 'eager' : 'lazy'"
                :decoding="preload ? 'sync' : 'async'"
                provider="storyblok"
                format="webp"
                :alt="props.alt"
                :class="[
                    imageHeight && !imageWidth ? 'h-full w-auto' : '',
                    imageWidth && !imageHeight ? 'h-auto w-full' : '',
                    !imageHeight && !imageWidth ? 'h-full w-full' : '',
                    hasObjectCoverClass ? 'object-cover' : 'object-contain',
                ]"
                :width="nuxtWidth"
                :height="nuxtHeight"
                @load="setLoaded"
                @error="setLoaded"
            />

            <transition name="fade">
                <div
                    v-show="
                        !imageLoaded && !preload && showSpinnerThresholdReached
                    "
                    class="absolute inset-0 flex items-center justify-center bg-white transition-all"
                >
                    <Spinner class="text-3xl" />
                </div>
            </transition>
        </picture>

        <img
            v-else
            :src="src"
            :alt="alt"
            class="h-full w-full object-contain"
            width="150"
            height="50"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useAttrs } from 'vue';

const props = withDefaults(
    defineProps<{
        src: string;
        alt?: string;
        sizes?: string;
        quality?: number;
        preload?: boolean;
        imageWidth?: number;
        imageHeight?: number;
        prioritizeHeight?: boolean;
    }>(),
    {
        alt: '',
        sizes: '120px',
        quality: 90,
        preload: false,
    },
);

const attrs = useAttrs();
const imageLoaded = ref(false);
const showSpinnerThresholdReached = ref(false);
let img: HTMLImageElement | null = null;

const hasObjectCoverClass = computed(() => {
    return (
        (attrs.class as string)?.split(' ').includes('object-cover') ?? false
    );
});

const parsedWidths = computed(() => {
    if (!props.sizes) return [];
    const regex = /(\d+)(?=px)/g;
    const matches = [...props.sizes.matchAll(regex)].map((m) => Number(m[1]));
    if (matches.length === 0) return [];
    if (matches[0] > 200) matches.unshift(200);
    return matches;
});

const defaultWidth = computed(() =>
    parsedWidths.value.length ? Math.max(...parsedWidths.value) : 1024,
);

const minWidth = computed(() =>
    parsedWidths.value.length ? Math.min(...parsedWidths.value) : 0,
);

const shouldPrioritizeHeight = computed(() =>
    Boolean(props.prioritizeHeight && props.imageHeight),
);

const nuxtWidth = computed<number | undefined>(() =>
    shouldPrioritizeHeight.value
        ? undefined
        : props.imageWidth || defaultWidth.value,
);

const nuxtHeight = computed<number | undefined>(() => {
    return shouldPrioritizeHeight.value
        ? props.imageHeight
        : props.imageHeight || fallbackHeight.value;
});

const fallbackHeight = computed(() => {
    if (props.imageHeight) return props.imageHeight;
    return props.imageWidth ? Math.round(props.imageWidth * 0.5625) : 600;
});

const optimizedImageUrl = ({
    width = 0,
    height = 0,
    format = 'webp',
}: {
    width?: number;
    height?: number;
    format?: string;
}) => {
    return `${props.src}/m/fit-in/${width}x${height}/filters:format(${format}):quality(${props.quality})`;
};

const srcset = computed(() => {
    if (shouldPrioritizeHeight.value && props.imageHeight) {
        return [
            `${optimizedImageUrl({ width: 0, height: props.imageHeight })} 1x`,
            `${optimizedImageUrl({ width: 0, height: props.imageHeight * 2 })} 2x`,
        ].join(', ');
    }

    const widths = new Set();

    parsedWidths.value.forEach((w) => widths.add(w));

    parsedWidths.value.forEach((w) => widths.add(w * 2));

    return Array.from(widths)
        .sort((a, b) => (a as number) - (b as number))
        .map((w) => {
            const apiWidth = props.imageHeight && !props.imageWidth ? 0 : w;
            const apiHeight = props.imageHeight
                ? props.imageHeight *
                  (w / (props.imageWidth || defaultWidth.value))
                : 0;
            return `${optimizedImageUrl({ width: apiWidth, height: Math.round(apiHeight) })} ${w}w`;
        })
        .join(', ');
});

const defaultUrl = computed(() => {
    if (shouldPrioritizeHeight.value && props.imageHeight) {
        return optimizedImageUrl({
            width: 0,
            height: props.imageHeight,
        });
    }

    const apiWidth =
        props.imageHeight && !props.imageWidth ? 0 : defaultWidth.value;
    return optimizedImageUrl({
        width: apiWidth,
        height: props.imageHeight || 0,
    });
});

const setLoaded = () => {
    imageLoaded.value = true;
};

onMounted(() => {
    setTimeout(() => {
        if (!imageLoaded.value) {
            showSpinnerThresholdReached.value = true;
        }
    }, 300);

    img = new Image();
    img.src = defaultUrl.value;
    if (img.complete) {
        setLoaded();
    } else {
        img.onload = setLoaded;
        img.onerror = setLoaded;
    }
});

onBeforeUnmount(() => {
    if (img) {
        img.onload = null;
        img.onerror = null;
    }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
